/* eslint-disable */
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import auth0 from "auth0-js";

const authConnection =
  process.env.VUE_APP_Auth_Connection ||
  process.env.VUE_APP_Auth_Realm ||
  "Username-Password-Authentication";
const authDomain = process.env.VUE_APP_Auth_Domain;
const authClientId = process.env.VUE_APP_Auth_Client_id;

const webAuth = new auth0.WebAuth({
  domain: authDomain,
  clientID: authClientId,
  scope: "openid profile email offline_access",
  responseType: "token id_token",
  useRefreshTokens: true,
});

function hasAuthHash() {
  const hash = window.location.hash || "";
  return (
    hash.includes("access_token=") ||
    hash.includes("id_token=") ||
    hash.includes("error=")
  );
}

function clearAuthHash() {
  window.history.replaceState(
    {},
    document.title,
    window.location.pathname + window.location.search
  );
}

function getAuthRedirectUri() {
  return `${window.location.origin}/`;
}

function getAuthConfigError() {
  if (!authDomain || !authClientId) {
    return "Auth0 domain or client ID is missing in .env.";
  }

  // if (!authDomain.includes(".auth0.com")) {
  //   return "Auth0 domain in .env does not look valid.";
  // }

  return "";
}

function getLoginErrorMessage(error) {
  const description =
    error && (error.description || error.errorDescription || error.message);

  if (error && error.error === "invalid_request" && description === "Unknown client.") {
    return "Auth0 rejected this client ID for the configured domain. Check VUE_APP_Auth_Domain and VUE_APP_Auth_Client_id in .env.";
  }

  if (description === "Wrong email or password.") {
    return "Invalid email or password.";
  }

  if (
    description === "please change your password" ||
    description === "Your password has been expired. Please reset your password."
  ) {
    return "Your password has expired. Please reset your password.";
  }

  if (description && description.toLowerCase().includes("state does not match")) {
    return "Authentication redirect mismatch. Please try signing in again.";
  }

  return description || "Unable to login. Please try again.";
}

export default {
  name: "login",
  setup() {
    const router = useRouter();
    const store = useStore();

    const username = ref("");
    const password = ref("");
    const errr = ref("");
    const loadingBlock = ref(false);
    const errorBlock = ref(false);
    const inputType = ref("password");
    const visibleOn = ref(false);

    function showLoginError(error) {
      errorBlock.value = true;
      errr.value = getLoginErrorMessage(error);
      loadingBlock.value = false;
    }

    async function completeLogin(authResult) {
      await store.dispatch("loginuser", authResult);
      await store.dispatch("setNavbar", true);
      await router.replace({ name: "Home" });
      loadingBlock.value = false;
    }

    async function handleAuthCallback() {
      if (!hasAuthHash()) {
        return;
      }

      errorBlock.value = false;
      errr.value = "";
      loadingBlock.value = true;

      await new Promise((resolve) => {
        webAuth.parseHash({ hash: window.location.hash }, async (error, authResult) => {
          clearAuthHash();

          if (error) {
            showLoginError(error);
            resolve();
            return;
          }

          if (!authResult || !authResult.accessToken || !authResult.idToken) {
            showLoginError(new Error("Auth0 did not return a valid login session."));
            resolve();
            return;
          }

          try {
            await completeLogin(authResult);
          } catch (sessionError) {
            showLoginError(sessionError);
          } finally {
            resolve();
          }
        });
      });
    }

    async function login() {
      errorBlock.value = false;
      errr.value = "";

      const configError = getAuthConfigError();
      if (configError) {
        showLoginError(new Error(configError));
        return;
      }

      if (!username.value) {
        showLoginError(new Error("The email field is required."));
        return;
      }

      if (!password.value) {
        showLoginError(new Error("The password field is required."));
        return;
      }

      loadingBlock.value = true;

      webAuth.login(
        {
          realm: authConnection,
          username: username.value.trim(),
          password: password.value,
          redirectUri: getAuthRedirectUri(),
          responseType: "token id_token",
          scope: "openid profile email",
        },
        (error) => {
          if (error) {
            showLoginError(error);
          }
        }
      );
    }

    function passwordVisible() {
      visibleOn.value = !visibleOn.value;
      inputType.value = visibleOn.value ? "text" : "password";
    }

    onMounted(handleAuthCallback);

    return {
      login,
      username,
      password,
      errr,
      loadingBlock,
      errorBlock,
      inputType,
      visibleOn,
      passwordVisible,
    };
  },
};
